import type { Directive } from "vue";

interface StickyElementEl extends HTMLElement {
  __stickyEl?: HTMLElement;
  __stickyOnScroll?: (evt: Event) => void;
  __stickyScrollParent?: HTMLElement;
}

/**
 * Walks up the DOM tree from `node` to find the nearest ancestor with
 * overflow auto/scroll, so the scroll parent is found even before content loads.
 */
function getScrollParent(node: HTMLElement): HTMLElement | undefined {
  let currentNode = node.parentNode as HTMLElement | null;

  while (currentNode) {
    const overflow = window.getComputedStyle(currentNode).overflow;
    if (overflow === "auto" || overflow === "scroll") {
      return currentNode;
    }
    if (!currentNode.parentNode) break;
    currentNode = currentNode.parentNode as HTMLElement;
  }

  return undefined;
}

function attachScrollListener(el: HTMLElement): void {
  const target = el as StickyElementEl;
  target.__stickyEl = el;
  const scrollParent = getScrollParent(el);

  if (scrollParent && !target.__stickyOnScroll) {
    target.__stickyOnScroll = (evt: Event) => {
      if (target.__stickyEl) {
        const scrollTarget = evt.target as HTMLElement;
        if (scrollTarget.scrollTop > 40) {
          target.__stickyEl.classList.add("stuck");
        } else if (scrollTarget.scrollTop === 0) {
          target.__stickyEl.classList.remove("stuck");
        }
      }
    };
    scrollParent.addEventListener("scroll", target.__stickyOnScroll);
    target.__stickyScrollParent = scrollParent;
  }
}

const StickyElement: Directive<HTMLElement> = {
  mounted(el) {
    attachScrollListener(el);
  },

  updated(el) {
    attachScrollListener(el);
  },

  unmounted(el) {
    const target = el as StickyElementEl;
    if (target.__stickyScrollParent && target.__stickyOnScroll) {
      target.__stickyScrollParent.removeEventListener("scroll", target.__stickyOnScroll);
    }
    if (target.__stickyEl) {
      target.__stickyEl.classList.remove("stuck");
    }
    delete target.__stickyEl;
    delete target.__stickyOnScroll;
    delete target.__stickyScrollParent;
  }
};

export default StickyElement;
